import React, { useState } from "react";
import { Icon } from "@iconify/react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/dist/geosearch.css";
import L from "leaflet";
import PropertyForm from "./newproperty";
import ForrentPropertyForm from "./addforent";
import { EditSaleProperty } from "./editSale";
import { EditSoldProperty } from "./editSold";
import { EditRentedProperty } from "./editRented";
import { EditRentProperty } from "./editRent";
import { useProperties } from "../hooks/useProperty";

// Custom Marker Icon Fix for Leaflet
const customIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
  shadowSize: [41, 41],
});

// Component that handles click events on the map
const MapClickHandler = ({ setCoordinates }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setCoordinates([lat.toFixed(6), lng.toFixed(6)]);
    },
  });
  return null;
};

// Adds the GeoSearch Control to the map
const SearchField = ({ setCoordinates }) => {
  const map = useMap();

  React.useEffect(() => {
    const provider = new OpenStreetMapProvider();
    const searchControl = new GeoSearchControl({
      provider,
      style: "bar",
      showMarker: true,
      showPopup: true,
      marker: {
        icon: customIcon,
      },
      popupFormat: ({ query, result }) => result.label,
      maxMarkers: 1,
      retainZoomLevel: false,
      animateZoom: true,
      keepResult: false,
    });

    map.addControl(searchControl);

    map.on("geosearch/showlocation", (event) => {
      const { x, y } = event.location; // x = lon, y = lat
      setCoordinates([y.toFixed(6), x.toFixed(6)]);
    });

    return () => map.removeControl(searchControl);
  }, [map, setCoordinates]);

  return null;
};

// Map component that displays the search bar and a marker
const MapWithSearch = ({ coordinates, setCoordinates }) => {
  return (
    <MapContainer
      center={[0, 0]}
      zoom={2}
      style={{ width: "100%", height: "100%" }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; OpenStreetMap contributors"
      />
      <SearchField setCoordinates={setCoordinates} />
      <Marker position={coordinates} icon={customIcon}>
        <Popup>Selected Location</Popup>
      </Marker>
      <MapClickHandler setCoordinates={setCoordinates} />
    </MapContainer>
  );
};

// Map Modal for selecting coordinates
const MapModal = ({ setFormData, formData, setMapOpen }) => {
  const [currentCoords, setCurrentCoords] = useState([0, 0]);

  const handleConfirm = () => {
    setFormData({
      ...formData,
      coordinates: currentCoords.join(", "),
    });
    setMapOpen(false);
  };

  const handleClose = () => {
    setMapOpen(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4">
      <div className="bg-white w-full max-w-3xl h-3/4 relative p-4 rounded flex flex-col">
        {/* Close Icon Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 focus:outline-none"
          aria-label="Close Map Selector"
        >
          <Icon icon="mdi:close" width="24" height="24" />
        </button>

        <h3 className="text-lg font-bold mb-2">
          Search and Select Coordinates
        </h3>
        <div className="flex-grow relative">
          <MapWithSearch
            coordinates={currentCoords}
            setCoordinates={setCurrentCoords}
          />
        </div>
        <button
          onClick={handleConfirm}
          className="mt-4 bg-teal-500 text-white px-4 py-2 rounded hover:bg-teal-600 transition"
        >
          Confirm
        </button>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenForrent, setIsModalOpenForrent] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [propertyStatus, setPropertyStatus] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterListingStatus, setFilterListingStatus] = useState("");
  const { properties, propertyDelete, isLoading: loading } = useProperties();
  const open1Modal = () => setIsModalOpen(true);
  const close1Modal = () => setIsModalOpen(false);
  const open1ModalForrent = () => setIsModalOpenForrent(true);
  const close1ModalForrent = () => setIsModalOpenForrent(false);

  const [formData, setFormData] = useState({
    // String fields (nullable)
    unit_number: null,
    view: null,
    unit_type: null,
    status: null,
    rental_type: null,
    coordinates: null,
    compass_direction: null,
    street_name: null,
    post_street_direction: null,
    city: null,
    state: null,
    zip_code: null,
    zip_plus_four: null,
    county: null,
    county_name: null,
    front_door_facing: null,
    home_type: null,
    parking_type: null, // Will be stringified array
    parking_spaces: null,
    stories_inside_home: null,
    master_bedroom_floor: null,
    pool_type: null, // Will be stringified array
    outdoor_sitting: null, // Will be stringified array
    private_amenities: null, // Will be stringified array
    waterfront_type: null, // Will be stringified array
    dock_type: null,
    renovation_type: null, // Will be stringified array
    floor_type: null, // Will be stringified array
    roof_type: null,
    air_conditioning_type: null, // Will be stringified array
    construction_type: null, // Will be stringified array
    additional_rooms: null, // Will be stringified array
    interior_features: null, // Will be stringified array
    home_exterior_features: null,
    appliances: null,
    cw_listing_status: null,
    listing_type: null,
    commission_type: null,
    public_remarks: null,
    broker_remarks: null,
    rented_agent_first_name: null,
    rented_agent_last_name: null,
    rented_agent_license: null,
    selling_agent_license: null,
    sale_type: null,
    financing: null,
    selling_agent_first_name: null,
    selling_agent_last_name: null,

    // Number fields (nullable)
    bedrooms: null,
    bathrooms: null,
    square_feet: null,
    street_number: null,
    real_estate_tax_year: null,
    lot_size: null,
    waterfront_frontage: null,
    roof_age: null,
    half_bathrooms: null,
    commission_amount: null,
    commission_percentage: null,
    year_built: null,
    last_renovation_year: null, // Special case for Year
    total_square_footage: null,

    // Decimal fields (nullable)
    price_or_rent: null,
    real_estate_taxes: null,
    previous_sold_price: null,

    // Boolean fields (nullable with defaults)
    covered_garage: false,
    covered_carport: false,
    assigned_parking: false,
    private_pool: false,

    // Enum fields (with default values)
    renovation: "",
    waterfront: "No",
    boat_access: "NO",
    landlord_pays_tenant_agents: "NO",
    short_sale: "NO",
    lender_approved_shorts_sale: "NO",
    foreclosure: "NO",
    seller_pays_buyer_agents: "NO",

    // Date fields (nullable)
    date: null,
    previous_sold_date: null,

    // Required fields
    property_details_url: "",
  });

  const handleSearch = (e) => setSearchQuery(e.target.value.toLowerCase());

  const handleTypeFilterChange = (e) => setFilterType(e.target.value);

  const handleCategoryFilterChange = (e) => setFilterCategory(e.target.value);

  const handleListingStatusFilterChange = (e) =>
    setFilterListingStatus(e.target.value);

  const getPropertyCategory = (property) => {
    if (property?.status) return property.status;
    if (property?.cw_listing_status === "SOLD") return "Sold";
    if (property?.cw_listing_status === "RENTED") return "Rented";
    if (property?.listing_type === "Residential Rental") return "Rent";
    if (property?.listing_type === "Residential Sale") return "Sale";
    return "";
  };

  const openModal = (property = null) => {
    setCurrentProperty(property);
    setPropertyStatus(property.status);
    const { id, ...cleanedProperty } = property;
    setFormData({
      ...formData,
      ...cleanedProperty,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentProperty(null);
    setPropertyStatus(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      propertyDelete(id);
    }
  };

  const filteredProperties = Array.isArray(properties)
    ? properties.filter(
        (property) =>
          (!searchQuery ||
            Object.values(property).some((value) =>
              String(value).toLowerCase().includes(searchQuery.toLowerCase())
            )) &&
          (!filterType || property.listing_type === filterType) &&
          (!filterCategory || getPropertyCategory(property) === filterCategory) &&
          (!filterListingStatus ||
            property.cw_listing_status === filterListingStatus)
      )
    : [];

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // Toggle direction
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedProperties = React.useMemo(() => {
    if (!sortConfig.key) return filteredProperties;
    const sorted = [...filteredProperties].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? "";
      const bValue = b[sortConfig.key] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }
      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return sorted;
  }, [filteredProperties, sortConfig]);

  // const currentFields = formSteps[currentStep];

  const buttonLabel = [
    {
      label: "ADD SALE (RESIDENTIAL)",
      modal: open1Modal,
      title: "Add New Residential Property (For Sale / Sold)",
    },
    {
      label: "ADD RENTAL (RESIDENTIAL)",
      modal: open1ModalForrent,
      title: "Add New Residential Property (For Rent / Rented)",
    },
  ];
  const statusMap = {
    "FOR SALE": "Sale",
    "FOR RENT": "Rent",
    RENTED: "Rented",
    SOLD: "Sold",
  };

  const getFilteredCount = (buttonIndex) => {
    if (buttonIndex === 0) {
      // Count sale and sold properties
      return (
        sortedProperties?.filter(
          (property) =>
            property.listing_type === "Residential Sale" ||
            property.cw_listing_status === "SOLD"
        ).length || 0
      );
    } else if (buttonIndex === 1) {
      // Count rental and rented properties
      return (
        sortedProperties?.filter(
          (property) =>
            property.listing_type === "Residential Rental" ||
            property.cw_listing_status === "RENTED"
        ).length || 0
      );
    }
    return 0;
  };

  // Calculate stats for Sale section
  const getSaleStats = () => {
    const saleProperties =
      properties?.filter(
        (property) => property.listing_type === "Residential Sale"
      ) || [];

    const total = saleProperties.length;
    const active = saleProperties.filter(
      (p) => p.cw_listing_status === "ACTIVE"
    ).length;
    const sold = saleProperties.filter(
      (p) => p.cw_listing_status === "SOLD"
    ).length;
    const others = total - active - sold;

    return { total, active, sold, others };
  };

  // Calculate stats for Rental section
  const getRentalStats = () => {
    const rentalProperties =
      properties?.filter(
        (property) => property.listing_type === "Residential Rental"
      ) || [];

    const total = rentalProperties.length;
    const active = rentalProperties.filter(
      (p) => p.cw_listing_status === "ACTIVE"
    ).length;
    const rented = rentalProperties.filter(
      (p) => p.cw_listing_status === "RENTED"
    ).length;
    const others = total - active - rented;

    return { total, active, rented, others };
  };

  return (
    <div className="container mx-auto px-4 pt-5">
      <h2 className="lg:text-2xl text-xl mb-2 font-bold text-[#003049]">
        Listings
      </h2>

      <div className="max-w-screen-xl py-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-stretch md:grid-cols-2 md:gap-8">
          {buttonLabel.map((button, index) => (
            <div
              className="flex flex-col items-center break-words"
              key={button.label}
            >
              <button
                onClick={() => {
                  button.modal();
                  setTitle(button?.title);
                }}
                className="w-full sm:w-auto bg-white border border-[#003049]  px-4 py-2 text-base font-bold rounded hover:bg-[#002f4995] uppercase text-center text-red-600"
              >
                {button.label}
              </button>
              <div className="mt-2 text-center">
                <div className="text-xs text-gray-600 mb-1">
                  {index === 0
                    ? "Total/Active/Sold/Others"
                    : "Total/Active/Rented/Others"}
                </div>
                <div className="font-semibold text-lg">
                  {index === 0
                    ? // Sale section stats: Total/Active/Sold/Others
                      `${getSaleStats().total}/${getSaleStats().active}/${
                        getSaleStats().sold
                      }/${getSaleStats().others}`
                    : // Rental section stats: Total/Active/Rented/Others
                      `${getRentalStats().total}/${getRentalStats().active}/${
                        getRentalStats().rented
                      }/${getRentalStats().others}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Search and Actions */}
      <div className="mb-4 flex flex-wrap gap-4 justify-between items-center mt-2 lg:mt-0">
        <input
          type="text"
          placeholder="Search..."
          className="flex-grow sm:flex-none w-full sm:w-1/4 border border-gray-300 rounded px-4 py-2"
          onChange={handleSearch}
        />
        <select
          className="w-full sm:w-1/4 border border-gray-300 rounded px-4 py-2 focus:rign-1 focus:outline-none"
          onChange={handleTypeFilterChange}
          value={filterType}
        >
          <option value="">All Listing Types</option>
          <option value="Residential Sale">Residential Sale</option>
          <option value="Residential Rental">Residential Rental</option>
        </select>
        <select
          className="w-full sm:w-1/4 border border-gray-300 rounded px-4 py-2 focus:rign-1 focus:outline-none"
          onChange={handleCategoryFilterChange}
          value={filterCategory}
        >
          <option value="">All Categories</option>
          <option value="Sale">Sale</option>
          <option value="Rent">Rent</option>
          <option value="Sold">Sold</option>
          <option value="Rented">Rented</option>
        </select>
        <select
          className="w-full sm:w-1/4 border border-gray-300 rounded px-4 py-2 focus:rign-1 focus:outline-none"
          onChange={handleListingStatusFilterChange}
          value={filterListingStatus}
        >
          <option value="">All Listing Statuses</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="PENDING">PENDING</option>
          <option value="SOLD">SOLD</option>
          <option value="RENTED">RENTED</option>
          <option value="OFF MARKET">OFF MARKET</option>
          <option value="CANCELLED">CANCELLED</option>
          <option value="WITHDRAWN">WITHDRAWN</option>
          <option value="EXPIRED">EXPIRED</option>
          <option value="INCOMPLETE">INCOMPLETE</option>
        </select>
        {(filterType || filterCategory || filterListingStatus) && (
          <button
            onClick={() => {
              setFilterType("");
              setFilterCategory("");
              setFilterListingStatus("");
            }}
            className="w-full sm:w-auto px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Filter Summary */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="text-sm text-blue-800">
          <strong>Active Filters:</strong>
          {filterType || filterCategory || filterListingStatus ? (
            <>
              {filterType && (
                <span className="ml-2 px-2 py-1 bg-blue-200 rounded">
                  Listing Type: {filterType}
                </span>
              )}
              {filterCategory && (
                <span className="ml-2 px-2 py-1 bg-blue-200 rounded">
                  Category: {filterCategory}
                </span>
              )}
              {filterListingStatus && (
                <span className="ml-2 px-2 py-1 bg-blue-200 rounded">
                  Listing Status: {filterListingStatus}
                </span>
              )}
            </>
          ) : (
            <span className="ml-2 text-blue-600">No filters active</span>
          )}
          <span className="ml-2 text-blue-600">
            Showing {filteredProperties.length} of {properties?.length || 0}{" "}
            properties
          </span>
        </div>
      </div>

      {/* Table or Loader */}
      {loading ? (
        <div className="flex h-screen justify-center items-center">
          <i
            className="pi pi-spin pi-spinner text-red-700"
            style={{ fontSize: "2rem" }}
          ></i>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-blue-300 text-sm text-left">
            <thead>
              <tr className="bg-blue-100">
                <th className="border border-black p-2">#</th>
                <th className="border border-black p-2">EDIT</th>
                <th
                  className="border border-black p-2 cursor-pointer select-none"
                  onClick={() => handleSort("short_address")}
                >
                  SHORT ADDRESS
                </th>
                <th
                  className="border border-black p-2 cursor-pointer select-none"
                  onClick={() => handleSort("unit_number")}
                >
                  UNIT #
                </th>
                <th
                  className="border border-black p-2 cursor-pointer select-none"
                  onClick={() => handleSort("listing_type")}
                >
                  LISTING TYPE
                </th>
                <th
                  className="border border-black p-2 cursor-pointer select-none"
                  onClick={() => handleSort("cw_listing_status")}
                >
                  LISTING STATUS
                  {/* {sortConfig.key === "cw_listing_status" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""} */}
                </th>
                <th
                  className="border border-black p-2 cursor-pointer select-none"
                  onClick={() => handleSort("price_or_rent")}
                >
                  PRICE
                </th>
                <th className="border border-black p-2">DELETE</th>
              </tr>
            </thead>
            <tbody>
              {sortedProperties.map((property, index) => (
                <tr key={property.id} className="hover:bg-gray-100">
                  <td className="border border-black p-2 text-center font-medium">
                    {index + 1}
                  </td>
                  <td className="border border-black p-2 flex items-center justify-center">
                    <Icon
                      onClick={() => openModal(property)}
                      icon="basil:edit-outline"
                      width="32"
                      height="32"
                      className="bg-[#003049] text-white p-1 rounded cursor-pointer"
                    />
                  </td>
                  <td className="border border-black p-2 uppercase">
                    {property.short_address ||
                      [property.street_number, property.street_name]
                        .filter(Boolean)
                        .join(" ")}
                  </td>
                  <td className="border border-black p-2 uppercase">
                    {property.unit_number}
                  </td>

                  <td className="border border-black p-2 uppercase">
                    {property.listing_type}
                  </td>
                  <td className="border border-black p-2 uppercase">
                    {property.cw_listing_status}
                  </td>
                  <td className="border border-black p-2 uppercase">
                    {property.price_or_rent
                      ? new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(property.price_or_rent)
                      : "-"}
                  </td>

                  <td className="border border-black p-2 flex items-center justify-center">
                    {!["SOLD", "RENTED"].includes(property.cw_listing_status) ? (
                      <Icon
                        onClick={() => handleDelete(property.id)}
                        icon="material-symbols-light:delete-outline"
                        width="32"
                        height="32"
                        className="bg-red-600 text-white p-1 rounded cursor-pointer"
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Map Modal */}
      {mapOpen && (
        <MapModal
          setFormData={setFormData}
          formData={formData}
          setMapOpen={setMapOpen}
        />
      )}

      <PropertyForm isOpen={isModalOpen} title={title} onClose={close1Modal} />
      {currentProperty && currentProperty.status === "Sale" && (
        <EditSaleProperty
          isOpen={modalOpen}
          title={"Edit Sale Property"}
          onClose={closeModal}
          property={currentProperty}
        />
      )}
      {currentProperty && currentProperty.status === "Rented" && (
        <EditRentedProperty
          isOpen={modalOpen}
          title={"Edit Rented Property"}
          onClose={closeModal}
          property={currentProperty}
        />
      )}
      {currentProperty && currentProperty.status === "Rent" && (
        <EditRentProperty
          isOpen={modalOpen}
          title={"Edit Rental Property"}
          onClose={closeModal}
          property={currentProperty}
        />
      )}
      {currentProperty && currentProperty.status === "Sold" && (
        <EditSoldProperty
          isOpen={modalOpen}
          title={"Edit Sold Property"}
          onClose={closeModal}
          property={currentProperty}
        />
      )}
      <ForrentPropertyForm
        isOpen={isModalOpenForrent}
        onClose={close1ModalForrent}
        title={title}
      />
    </div>
  );
};

export default AdminPanel;
